import { Box, Button, CircularProgress, Container, Typography } from '@mui/material'
import { useAuth } from 'hooks/use-auth'
import { useEffect, useState } from 'react'
import { sendGet, sendPost } from 'hooks/use-fetch'
import FamilyIcon from '@mui/icons-material/FamilyRestroom'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { SearchIcon } from 'Assets/SVGs/SearchIcon'
import { generatePath, useNavigate } from 'react-router'
import { CAREGIVER_PORTAL_CARE_MANAGEMENT_SETUP, CAREGIVER_PORTAL_MEMBERS, CAREGIVER_PORTAL_MEMBERS_SERVICE_DETAILS, CAREGIVER_PORTAL_SERVICE_FINDER } from 'Pages/Routes'
import { useScrollTop } from 'hooks/use-scrollTop'
import { EnrollmentCard } from 'Components/Cards/CaregiverPortal/EnrollmentCard'
import { type carenetworkService } from 'core/types'
import { type notificationEvent } from 'Pages/CareNetworkManagement/CareNetworkDetail/ChildHistoryDisplay'
import { DashboardNotificationCard } from './DashboardNotificationCard'
import { CareManagementContactCard } from './CareManagementContactCard'
import { type CaregiverDashboardData } from './types'

// unread notifications older than this are no longer listed on the dashboard
const RECENT_NOTIFICATION_DAYS = 30

const sectionHeaderSx = { color: '#222024', fontSize: '18px', fontWeight: 500, mt: '24px', mb: '12px' }
const cardSx = { backgroundColor: '#ECEDEF', borderRadius: '10px' }

export function CaregiverDashboard (): JSX.Element {
  const nav = useNavigate()
  const { user } = useAuth()
  const [dashboard, setDashboard] = useState<CaregiverDashboardData | null>(null)

  useScrollTop()

  useEffect(() => {
    async function loadData (): Promise<void> {
      const { response, success } = await sendGet('/CaregiverPortal/GetDashboard', {})
      if (success) {
        setDashboard(response)
      }
    }
    if (user != null) {
      void loadData()
    }
  }, [user])

  const handleNotificationOpened = async (event: notificationEvent): Promise<void> => {
    if (!event.viewed) {
      await sendPost(`/User/MarkEventViewed?eventId=${event.id}`, {})
      event.viewed = true
    }
  }

  const openEnrollment = (enrollment: carenetworkService): void => {
    nav(generatePath(CAREGIVER_PORTAL_MEMBERS_SERVICE_DETAILS, { enrollmentId: String(enrollment.enrollmentID) }))
  }

  if (user == null) {
    return <Typography variant='body1' sx={{ fontWeight: 600, mt: '20px' }} component='div'>
      Please log in to view your dashboard.
    </Typography>
  }

  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - RECENT_NOTIFICATION_DAYS)
  const recentNotifications = user.notifications
    .filter(n => n.isNew && new Date(n.eventDate) >= cutoff)
    .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime())

  const schoolYears = dashboard?.schoolYears ?? []
  const actionRequired = schoolYears.some(y => y.children.some(c => c.enrollments.some(e => e.requiresAction)))

  return <Container sx={{ mb: '30px' }}>
    <Typography variant='h5' sx={{ fontWeight: 600, mt: '20px' }} component='div'>
      Welcome back, {user.firstName}.
    </Typography>

    {actionRequired && (
      <Box data-testid='actionRequiredBanner' sx={{ display: 'flex', alignItems: 'center', gap: '16px', mt: '16px', px: '20px', py: '12px', border: '1px solid #BC3656', borderRadius: '8px', backgroundColor: '#FDECEF' }}>
        <ErrorOutlineIcon sx={{ color: '#BC3656' }} />
        <Typography sx={{ color: '#222024', fontSize: '12px' }}>
          There are some actions that are required from you to get your requested services completed. Please review the service details below for more information.
        </Typography>
      </Box>
    )}

    <Typography sx={sectionHeaderSx} component='h2'>Recent Notifications</Typography>
    {recentNotifications.length > 0
      ? <Box data-testid='recentNotifications' sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {recentNotifications.map(n => <DashboardNotificationCard key={n.id} event={n} onOpen={(e) => { void handleNotificationOpened(e) }} />)}
      </Box>
      : <EmptyCard
          testId='notificationsEmpty'
          icon={<NotificationsNoneIcon sx={{ color: '#2C7A7B' }} />}
          iconBackground='#E0F2F1'
          title="You're all caught up!"
          description='No new notifications right now. Check back later for updates.'
      />
    }

    {dashboard == null && <Box sx={{ mt: '24px' }}><CircularProgress /></Box>}
    {dashboard != null && schoolYears.length === 0 && (
      <Box sx={{ mt: '16px' }}>
        <EmptyCard
          testId='enrollmentsEmpty'
          icon={<AssignmentOutlinedIcon sx={{ color: '#7136BC' }} />}
          iconBackground='#F1EAFB'
          title='No active enrollments'
          description='When you start an enrollment, it will appear here. You can also use Quick Links to find services and manage your care network.'
        />
      </Box>
    )}
    {schoolYears.map(year => (
      <Box key={year.timeSpanID ?? 'none'} data-testid={`schoolYear-${year.timeSpanID ?? 'none'}`}>
        <Typography sx={sectionHeaderSx} component='h2' data-testid='schoolYearHeader'>
          {schoolYearTitle(year.name)}
        </Typography>
        {year.children.map(child => (
          <Box key={child.childID} data-testid={`schoolYearChild-${child.childID}`} sx={{ mb: '16px' }}>
            <Typography sx={{ color: '#222024', fontSize: '16px', mb: '12px' }} data-testid='childName'>{child.childName}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {child.enrollments.map(enrollment => (
                <Box key={enrollment.enrollmentID} data-testid={`dashboardEnrollment-${String(enrollment.enrollmentID)}`}>
                  <EnrollmentCard service={enrollment} onClick={openEnrollment} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    ))}

    {dashboard != null && dashboard.careManagementContacts.length > 0 && (
      <Box sx={{ mt: '16px' }}>
        <CareManagementContactCard contacts={dashboard.careManagementContacts} />
      </Box>
    )}
    {dashboard?.canRequestCareManagement === true && (
      <Box sx={{ mt: '16px' }}>
        <EmptyCard
          testId='careManagementHelpCard'
          icon={<FavoriteBorderIcon sx={{ color: '#7136BC' }} />}
          iconBackground='#F1EAFB'
          title='Care Management'
          description='Need support? Our team is here to help.'
          action={
            <Button
              variant='outlined'
              data-testid='getHelpButton'
              onClick={() => { nav(CAREGIVER_PORTAL_CARE_MANAGEMENT_SETUP) }}
              sx={{ mt: '8px', borderRadius: '32px', borderColor: '#7136BC', color: '#7136BC', textTransform: 'none', fontSize: '16px', px: '28px' }}
            >
              Click here to get help
            </Button>
          }
        />
      </Box>
    )}

    <Typography sx={sectionHeaderSx} component='h2'>Quick Links</Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <QuickLinkCard
        testId='members-card'
        title='Members'
        description='Click here to manage your care network members'
        icon={<FamilyIcon sx={{ fontSize: '48px', color: '#222024' }} />}
        onClick={() => { nav(CAREGIVER_PORTAL_MEMBERS) }}
      />
      <QuickLinkCard
        testId='find-services-card'
        title='Find additional services'
        description='Click here to find additional services for one of your children'
        icon={<SearchIcon />}
        onClick={() => { nav(CAREGIVER_PORTAL_SERVICE_FINDER) }}
      />
    </Box>
  </Container>
}

// school years are named by admins, usually already ending in "School Year" (e.g. "2025/2026 School Year")
function schoolYearTitle (name: string | null): string {
  if (name == null || name.trim() === '') return 'Other Enrollments'
  return /school year$/i.test(name.trim()) ? name.trim() : `${name.trim()} School Year`
}

interface EmptyCardProps {
  testId: string
  icon: JSX.Element
  iconBackground: string
  title: string
  description: string
  action?: JSX.Element
}

function EmptyCard (props: EmptyCardProps): JSX.Element {
  return (
    <Box data-testid={props.testId} sx={{ ...cardSx, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '8px', px: '20px', py: '24px' }}>
      <Box sx={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: props.iconBackground, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {props.icon}
      </Box>
      <Typography sx={{ color: '#222024', fontSize: '16px', fontWeight: 500 }}>{props.title}</Typography>
      <Typography sx={{ color: '#514F53', fontSize: '14px' }}>{props.description}</Typography>
      {props.action}
    </Box>
  )
}

interface QuickLinkCardProps {
  testId: string
  title: string
  description: string
  icon: JSX.Element
  onClick: () => void
}

function QuickLinkCard (props: QuickLinkCardProps): JSX.Element {
  return (
    <Box data-testid={props.testId} onClick={props.onClick} sx={{ ...cardSx, display: 'flex', alignItems: 'center', gap: '12px', px: '28px', py: '16px', cursor: 'pointer' }}>
      <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
        <Typography sx={{ color: '#222024', fontSize: '20px', fontWeight: 600, mb: '8px' }}>{props.title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {props.icon}
          <Typography sx={{ color: '#222024', fontSize: '12px' }}>{props.description}</Typography>
        </Box>
      </Box>
      <ChevronRightIcon sx={{ color: '#514F53' }} />
    </Box>
  )
}
